import { captureMessage } from '@sentry/react';
import queryString from 'query-string';

import {
  AdminSection,
  Params,
  UserInfoCluster,
  UserOrganization,
} from '@typings';
import { API_ADMIN } from '@constants';
import { axios } from '@features';
import { path } from '@utils';

export const agreeUserTerms = ({ username, email }: Params.AgreeUserTerms) => {
  return axios.put(`${API_ADMIN}/users/${username}/profile`, {
    terms_accepted: true,
    name: username,
    email,
  });
};

export const getUser = ({ adminUrl, username }: Params.GetUser) => {
  return axios.get<
    never,
    {
      termsAccepted?: boolean;
      clusters: UserInfoCluster[];
      orgs: UserOrganization[];
      systemRole?: AdminSection.User['role'];
    }
  >(`${adminUrl}/users/${username}`, {
    params: { include: ['clusters', 'system_role'] },
    paramsSerializer: (params) => queryString.stringify(params),
  });
};

export const getUserOrganizationsCredits = ({
  adminUrl,
  username,
}: Params.GetUserOrganizationCredits) =>
  axios
    .get<never, { orgs: UserOrganization[] }>(
      path.create(adminUrl, 'users', username),
      { params: { include: 'orgs' } },
    )
    .catch(() => {
      // TODO: need to handle the Sentry Error for the billing issue. When the server fixes the issue, we can remove this
      captureMessage(`Could not fetch ${username} user organizations`);
      return { orgs: [] };
    });

export const getUsers = () =>
  axios.get<never, AdminSection.User[]>(`${API_ADMIN}/users`);

export const getSystemUsers = () =>
  axios.get<never, AdminSection.User[]>(`${API_ADMIN}/system/users`);

export const setSystemUserRole = async ({
  name,
  role,
}: {
  name: string;
  role: AdminSection.Role;
}) =>
  axios.post<
    never,
    never,
    {
      name: AdminSection.User['name'];
      role: AdminSection.Role;
    }
  >(`${API_ADMIN}/system/users`, { name, role });

export const updateSystemUserRole = async ({
  name,
  role,
}: {
  name: string;
  role: AdminSection.Role;
}) =>
  axios.put<never, never, { role: AdminSection.Role }>(
    `${API_ADMIN}/system/users/${name}`,
    { role },
  );

export const removeSystemUserRole = async ({ name }: { name: string }) =>
  axios.delete<never, never, { role: AdminSection.Role }>(
    `${API_ADMIN}/system/users/${name}`,
  );
